---
title: Uvod
sidebar_position: 1
description: Kaj je Planet Area Monitoring, ključni pojmi, zahteve in dostop.
---

# Uvod

## Kaj je Planet Area Monitoring Service?

**Planet Area Monitoring Service (Planet AMS)** je oblačna rešitev, zasnovana za spremljanje in preverjanje dejavnosti skozi čas na kmetijskih in drugih zemljiščih. Storitev združuje javne podatke opazovanja Zemlje iz virov Sentinel-1 in Sentinel-2 z visokoločljivimi posnetki Planet, vključno z Analysis-Ready PlanetScope (ARPS) in Planetary Variables.

Planet AMS pretvarja neobdelane satelitske posnetke v dva ključna tipa analitičnih podatkov:

- **Signali (Signals)**: analiza časovnih vrst, agregirana za vsako območje zanimanja (FOI) iz satelitskih opazovanj, ki zajema, kako se razmere na polju spreminjajo skozi sezono
- **Markerji (Markers)**: napovedi pojavov, kot so gola tla, klasifikacija posevkov in dogodki košnje, izpeljane iz fenoloških vzorcev signalov

Ti podatki so predstavljeni prek **Area Monitoring Application (AM App)** — specializirane spletne aplikacije, ki ponuja intuitiven vmesnik, ki združuje posnetke, signale, Markerje, ocene (Evaluation), odločitve (Decision) (semaforske kazalnike) in pomožne podatke za takojšnje terensko preverjanje in preverjanje skladnosti.

![AM App](../imgs/am-app.png)

## Ključni pojmi

Pred uporabo aplikacije AM App je pomembno razumeti te osnovne pojme:

### Obseg

**Obseg (Scope)** določa geografske podatke in časovne intervale, ki so na voljo za spremljanje. Ko izberete obseg, izbirate:

- določeno geografsko regijo
- nabor območij zanimanja (FOI) znotraj te regije
- časovna obdobja, za katera so podatki na voljo
- kateri signali in Markerji se izračunavajo

Izbira obsega omeji vse nadaljnje možnosti filtriranja in določa, do katerih podatkov lahko dostopate.

### Območje zanimanja (Feature of Interest)

**Območje zanimanja (Feature of Interest, FOI)** je temeljni geometrijski element, ki se spremlja v sistemu. V večini primerov FOI predstavlja kmetijsko polje ali zemljiško parcelo. Vsako območje zanimanja ima:

- enolični identifikator
- geografske meje (poligonska geometrija)
- pridružene atribute (vrsta posevka, raba zemljišča, površina itd.)

### Signali

**Signali** so meritve časovnih vrst, izpeljane iz satelitskih opazovanj. Za vsako območje zanimanja se satelitski podatki obdelajo za izračun vrednosti, kot so:

- odbojnost pasov (npr. rdeči, zeleni, modri, bližnji infrardeči)
- vegetacijski indeksi (NDVI, EVI)
- vsebnost vlage
- raven klorofila
- druge spektralne meritve

Signali zagotavljajo neprekinjene podatke spremljanja, ki kažejo, kako se razmere na polju spreminjajo skozi čas.

### Markerji

**Markerji** so napovedne meritve, ki nakazujejo določene vzorce upravljanja ali značilnosti polja. Primeri vključujejo:

- zaznavanje golih tal
- klasifikacijo posevkov (določanje, kateri posevek raste)
- zaznavanje dogodkov košnje
- oceno homogenosti

Markerji so izpeljani iz fenoloških vzorcev signalov in pomagajo prepoznati ključne kmetijske dejavnosti in stanja polja.

Na osnovnih markerjih sta zgrajeni dve specializirani vrsti markerjev:

- **Markerji za ocenjevanje (Evaluation Markers)** uporabljajo signale in markerje za odgovor na določeno vprašanje da/ne, na primer "Ali je bilo to polje pokošeno julija?".
- **Markerji za odločanje (Decision Markers)** združujejo več Markerjev za ocenjevanje v nastavljivem odločitvenem drevesu in ustvarijo semaforsko oceno, usklajeno s poslovnimi pravili ali merili skladnosti organizacije.

### Kontekst markerjev

**Kontekst markerjev** obravnava več izračunov markerjev, ki lahko nastanejo v rastni sezoni. Med različnimi konteksti markerjev lahko preklapljate, da primerjate različne scenarije izračuna ali časovna obdobja (npr. mesečne iteracije).

## Sistemske zahteve

Za uporabo aplikacije AM App potrebujete:

**Spletni brskalnik**:

- sodoben spletni brskalnik (Chrome, Firefox, Edge, Safari)
- omogočen JavaScript

**Internetna povezava**:

- stabilna internetna povezava
- priporočeno: širokopasovna povezava za optimalno delovanje

**Dostop do računa**:

- račun Planet ALI namenske poverilnice za Area Monitoring Service
- ustrezna dovoljenja za obseg (nastavi jih administrator)

**Zaslon**:

- najmanjša priporočena ločljivost zaslona: 1366x768
- ni podprto na majhnih zaslonih (mobilni telefoni)

## Pravice dostopa in dovoljenja

Dostop do podatkov v aplikaciji AM App je nadzorovan z dovoljenji za obseg:

- Obseg `DEMO_FR23` je privzeto omogočen za vse uporabnike s standardnim računom Planet. Ta predstavitveni obseg vam omogoča raziskovanje funkcij aplikacije.
- Za dostop do dodatnih ali projektno specifičnih obsegov morate zaprositi za dostop.

Dovoljenja za obseg določajo:

- katera območja zanimanja si lahko ogledate
- katera časovna obdobja so na voljo
- kateri signali in Markerji se izračunavajo
- dostop do administrativnih funkcij (če je primerno)
